import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { MetricCard, MetricCardProps } from 'components';
import getRandomColor from 'helpers/getRandomColor';
import { formatCurrency } from 'helpers/formatters';
import { CampaignTypes } from 'api';
import { AttributionModelEnum } from 'types/common';
import { getAttributionModelField } from 'helpers/common';

interface Props {
  inline?: boolean;
  performance: CampaignTypes.PerformanceData;
  attributionModel: AttributionModelEnum;
}

export type CampaignInfoItems = ({
  key: keyof CampaignTypes.PerformanceSummary;
} & Omit<MetricCardProps, 'title'>)[];

const MetricsCards: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    performance: { summary },
    inline,
    attributionModel,
  } = props;

  const config: CampaignInfoItems = useMemo(
    () => [
      {
        key: 'budget_potential',
        subtitle: 'Potential budget',
        color: getRandomColor(1),
      },
      {
        key: 'budget_actual',
        subtitle: 'Actual budget',
        color: getRandomColor(11),
      },
      {
        key: 'revenue_potential',
        subtitle: 'Potential revenue',
        color: getRandomColor(5),
      },
      {
        key: getAttributionModelField(attributionModel, 'revenue_actual'),
        subtitle: 'Actual revenue',
        color: getRandomColor(6),
        subtitleCaption: `(${attributionModel})`,
      },
      {
        key: 'roi_potential',
        subtitle: 'Potential ROI',
        color: getRandomColor(9),
      },
      {
        key: getAttributionModelField(attributionModel, 'roi_actual'),
        subtitle: 'Actual ROI',
        color: getRandomColor(10),
        subtitleCaption: `(${attributionModel})`,
      },
    ],
    [attributionModel],
  );

  return (
    <Box>
      <Grid container spacing={2} justifyContent="center">
        {config.map((c) => (
          <Grid item xs={6} md={inline ? 6 : 3} key={c.key}>
            <MetricCard
              title={
                (c.key.includes('roi')
                  ? summary[c.key]
                  : formatCurrency(summary[c.key], 0)) ?? '-'
              }
              subtitle={c.subtitle}
              subtitleCaption={c.subtitleCaption}
              color={c.color}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default MetricsCards;
